import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import ListKeymap from '@tiptap/extension-list-keymap';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import { Plugin, PluginKey } from 'prosemirror-state';
import { isValidUrl, normalizeUrl } from '../../../../utils/urlUtils';
import { TaskAttachment } from '../../../../types/TaskAttachment';
import { MentionList } from './MentionList';

// Extension to handle image paste
const ImagePaste = Extension.create({
  name: 'imagePaste',

  addStorage() {
    return {
      onImageUpload: null as ((file: File) => Promise<TaskAttachment | null>) | null,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('imagePaste'),
        props: {
          handlePaste: (view, event: ClipboardEvent, slice) => {
            if (!event.clipboardData) return false;

            const items = Array.from(event.clipboardData.items);
            const imageItems = items.filter((item) => item.type.startsWith('image'));

            if (imageItems.length === 0) {
              return false;
            }

            event.preventDefault();

            imageItems.forEach((item) => {
              const blob = item.getAsFile();
              if (!blob) return;

              const onImageUpload = this.editor.storage.imagePaste.onImageUpload;

              if (onImageUpload && typeof onImageUpload === 'function') {
                onImageUpload(blob).then((attachment: TaskAttachment | null) => {
                  if (attachment) {
                    this.editor.commands.setImage({ src: attachment.url });
                  }
                });
              }
            });

            return true;
          },
          handleDOMEvents: {
            dragover: (view, event) => {
              // Prevent default to allow drop
              if (!event.dataTransfer) return false;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              return false;
            },
            drop: (view, event) => {
              if (!event.dataTransfer) return false;

              const files = Array.from(event.dataTransfer.files);
              const imageFiles = files.filter((file) => file.type.startsWith('image'));

              if (imageFiles.length === 0) {
                return false;
              }

              event.preventDefault();

              // Get drop position for better UX
              const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });

              const onImageUpload = this.editor.storage.imagePaste.onImageUpload;

              if (onImageUpload && typeof onImageUpload === 'function') {
                imageFiles.forEach((file) => {
                  onImageUpload(file)
                    .then((attachment: TaskAttachment | null) => {
                      if (attachment) {
                        // Insert at drop position if available, otherwise at cursor
                        if (pos) {
                          this.editor
                            .chain()
                            .focus()
                            .insertContentAt(pos.pos, { type: 'image', attrs: { src: attachment.url } })
                            .run();
                        } else {
                          this.editor.commands.setImage({ src: attachment.url });
                        }
                      }
                    })
                    .catch((error) => {
                      console.error('Failed to upload image via drag & drop:', error);
                    });
                });
              }

              return true;
            },
          },
        },
      }),
    ];
  },
});

// Custom keyboard shortcuts
export const CustomKeyboardShortcuts = Extension.create({
  name: 'customKeyboardShortcuts',

  addKeyboardShortcuts() {
    return {
      // Enter on an empty list item ends the list (one level per press). Tiptap's own
      // ListItem binding can't do this on its own: its splitListItem returns false for an
      // empty item, so without this the empty line stays in the list as a bullet-less
      // paragraph instead of outdenting.
      Enter: ({ editor }) => {
        const { $from } = editor.state.selection;
        const listItemType = editor.schema.nodes.listItem;

        for (let depth = $from.depth; depth > 0; depth -= 1) {
          if ($from.node(depth).type !== listItemType) continue;

          // The whole *item* must be empty, not just the paragraph holding the caret.
          // An item can also hold further paragraphs and a sublist, and liftListItem
          // lifts the entire item — so testing the paragraph alone pulled the item's
          // text and nested bullets out of the list with it. (#3034)
          const listItem = $from.node(depth);
          const isEmptyItem = listItem.childCount === 1 && listItem.firstChild?.content.size === 0;

          return isEmptyItem ? editor.commands.liftListItem('listItem') : false;
        }

        // Not in a list — default behavior.
        return false;
      },
      'Shift-Enter': ({ editor }) => {
        // Shift-Enter creates a new line. The callbacks must use the `commands` object
        // from the command props: calling editor.commands.* here dispatches outside this
        // command, so the keymap never reports a handled key and the browser also applies
        // its native insertLineBreak on top of ours, leaving a stray space (#2435).
        return editor.commands.first(({ commands }) => [
          () => commands.newlineInCode(),
          () => commands.splitListItem('listItem'),
          () => commands.createParagraphNear(),
          () => commands.liftEmptyBlock(),
          () => commands.splitBlock(),
        ]);
      },
      Tab: ({ editor }) => {
        const { $from } = editor.state.selection;
        const depth = $from.depth;

        // Limit the depth of the indentation
        if (depth >= 10) {
          return true;
        }

        // Check context - if we're in a list or code block
        const inList = editor.isActive('bulletList') || editor.isActive('orderedList') || editor.isActive('listItem');
        const inCodeBlock = editor.isActive('codeBlock');

        if (inList) {
          // In lists, allow default indentation behavior
          editor.commands.sinkListItem('listItem');
          return true;
        } else if (inCodeBlock) {
          // In code blocks, insert actual tab character or spaces
          return editor.commands.insertContent('\t');
        } else {
          // In regular text, insert tab character instead of navigating
          editor.commands.insertContent('\t');
          return true; // Prevent default Tab behavior
        }
      },
    };
  },
});

// Comment-specific keyboard shortcuts
export const CommentKeyboardShortcuts = Extension.create({
  name: 'commentKeyboardShortcuts',

  addStorage() {
    return {
      // Set by RichTextEditor (comment mode). Submits the comment on Cmd/Ctrl+Enter.
      onSubmit: null as (() => void) | null,
    };
  },

  addKeyboardShortcuts() {
    return {
      // Cmd/Ctrl+Enter submits the comment. Handled inside the editor's keymap so
      // it reliably takes precedence over the default Enter (splitBlock/newline)
      // behaviour — an external DOM keydown listener loses that race. (#637)
      'Mod-Enter': () => {
        const { onSubmit } = this.storage as { onSubmit: (() => void) | null };
        if (typeof onSubmit === 'function') {
          onSubmit();
          return true;
        }
        return false;
      },
      // For comments, plain Enter inserts a newline (default behaviour).
      Enter: () => false,
      'Shift-Enter': ({ editor }) => {
        // Shift-Enter creates a new line in comments. See the note on the description-mode
        // binding above: the callbacks must use `commands` from the props, not
        // editor.commands.*, or the browser adds a stray space (#2435).
        return editor.commands.first(({ commands }) => [
          () => commands.newlineInCode(),
          () => commands.splitListItem('listItem'),
          () => commands.createParagraphNear(),
          () => commands.liftEmptyBlock(),
          () => commands.splitBlock(),
        ]);
      },
    };
  },
});

// Suggestion configuration for mentions
export const getMentionSuggestion = (users: Array<{ id: number; display: string; profilePicturePath?: string }>) => ({
  items: ({ query }: { query: string }) => {
    return users.filter((user) => user.display.toLowerCase().includes(query.toLowerCase())).slice(0, 10); // Limit to 10 suggestions
  },

  render: () => {
    let component: ReactRenderer;
    let popup: any;

    return {
      onStart: (props: any) => {
        component = new ReactRenderer(MentionList, {
          props: {
            ...props,
            users,
            onExit: () => {
              // Exit mention mode by clearing the mention query and hiding the popup
              const { tr } = props.editor.state;
              props.editor.view.dispatch(tr);
              // Hide the popup
              if (popup && popup[0]) {
                popup[0].hide();
              }
            },
          },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'mention-suggestions',
          maxWidth: 300,
          offset: [0, 8],
        });
      },

      onUpdate: (props: any) => {
        component.updateProps({
          ...props,
          users,
          onExit: () => {
            // Exit mention mode by clearing the mention query and hiding the popup
            const { tr } = props.editor.state;
            props.editor.view.dispatch(tr);
            // Hide the popup
            if (popup && popup[0]) {
              popup[0].hide();
            }
          },
        });

        if (!props.clientRect) {
          return;
        }

        popup[0].setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown: (props: any) => {
        // Let MentionList handle all keyboard events including ESC
        return component.ref?.onKeyDown(props);
      },

      onExit: () => {
        popup[0].destroy();
        component.destroy();
      },
    };
  },
});

// Get rich text editor extensions
export const getRichTextEditorExtensions = (
  users: Array<{ id: number; display: string; profilePicturePath?: string }> = [],
  isCommentMode = false,
  currentUser?: { id: number; email?: string; fullName?: string | null }
) => [
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
    // For comments, we might want to disable some features, but let's keep them all for now
    // since the user wants all the same options
  }),
  // Without this, Backspace in a list falls through to ProseMirror's baseKeymap
  // (deleteSelection -> joinBackward -> selectNodeBackward). When joinBackward can't
  // merge across a list boundary, selectNodeBackward selects the whole preceding block
  // instead: the press appears to do nothing, the selection toolbar pops open, and the
  // next Backspace wipes that block. (#2654)
  ListKeymap,
  TextStyle,
  Color.configure({
    types: ['textStyle'],
  }),
  Placeholder.configure({
    placeholder: isCommentMode ? 'Write a comment...' : 'What this task is about?',
    emptyEditorClass: 'is-editor-empty',
  }),
  Link.configure({
    openOnClick: false,
    validate: (url) => {
      const normalizedUrl = normalizeUrl(url);
      return isValidUrl(normalizedUrl);
    },
    HTMLAttributes: {
      class: isCommentMode ? 'comment-link' : 'task-link',
    },
  }),
  Image.configure({
    HTMLAttributes: {
      class: 'tiptap-image',
      style: 'max-width: 100%', // Prevent images from exceeding container width
    },
  }),
  Underline,
  TaskList.configure({
    HTMLAttributes: {
      class: 'taskList',
    },
  }),
  TaskItem.configure({
    nested: false,
  }),
  // Add mention support if users are provided
  ...(users.length > 0
    ? [
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion: getMentionSuggestion(users),
          deleteTriggerWithBackspace: true,
          parseHTML() {
            return [
              {
                tag: 'span[data-type="mention"]',
                getAttrs: (element) => {
                  const id = element.getAttribute('data-id');
                  let label = element.textContent?.replace('@', '') || null;

                  // If we have a valid label from the HTML content, use it
                  // Otherwise, try to find the user from the users array
                  if (!label || label.trim() === '') {
                    const userId = parseInt(id || '0');
                    const user = users.find((u) => u.id === userId);
                    label = user?.display || null;
                  }

                  return { id, label };
                },
              },
            ];
          },
          renderHTML({ node }) {
            const { id, label } = node.attrs;
            // Render in the format expected by existing comment system: @[DisplayName](userId)
            let displayLabel = label;

            // If no label, try to find the user in the users array
            if (!displayLabel) {
              const userId = parseInt(id || '0');
              const user = users.find((u) => u.id === userId);
              displayLabel = user?.display || 'Unknown User';
            }

            // Determine if this mention is for the current user
            const mentionedUserId = parseInt(id || '0');
            const isCurrentUser = currentUser && currentUser.id === mentionedUserId;
            const mentionClass = isCurrentUser ? 'mention mention-self' : 'mention mention-other';

            return [
              'span',
              {
                class: mentionClass,
                'data-type': 'mention',
                'data-id': id,
              },
              `${displayLabel}`,
            ];
          },
        }),
      ]
    : []),
  // Use comment-specific shortcuts for comments, regular shortcuts for descriptions
  isCommentMode ? CommentKeyboardShortcuts : CustomKeyboardShortcuts,
  ImagePaste,
];
